import React from 'react';
import {
  CircularProgress,
  Fade,
  Typography,
} from '@mui/material';
import { Formik, Form } from 'formik';
import { useHistory } from 'react-router-dom';
import type { PLRFormProps } from '../../../../../../types/preloan';
import {
  getContactMessage, firstMessageSwitch,
  secondMessageSwitch, getRedirectUrl,
} from './components/messageSwitches';
import type { FormSchemaV3 } from '../../../../../../types/preloan/v3';
import analytics from '../../../../../../utils/analytics';
import { getOriginButtonLabel } from '../../formLogic';
import HTMLParser from '../../../../../../components/HTMLParser';
import ComplementLeadResultDialog from '../../../ComplementInvite/ComplementLeadResultDialog';
import {
  inviteePlrCategory,
  LEAD_OFFER_PAGE,
  leadResultCategory,
} from '../../../ComplementInvite/analyticsPages';

export default function LastStepForm<T extends FormSchemaV3>(props: PLRFormProps<T>) {
  const { formRef, formSchema, handleEnableLoading } = props;
  const history = useHistory();
  const leadCategory = leadResultCategory(formSchema.isRedirect);
  const alreadyEvaluatedInvitee = Boolean(formSchema.complementToken)
    && formSchema.isRedirect === 'REDIRECCION RUT'
    && typeof formSchema.leadComplementAccepted !== 'boolean';
  const showComplementOffer = Boolean(formSchema.complementOfferAvailable)
    && typeof formSchema.leadComplementAccepted !== 'boolean'
    && !alreadyEvaluatedInvitee;
  React.useEffect(() => handleEnableLoading(false), []);
  React.useEffect(() => {
    const resultCategory = inviteePlrCategory(
      formSchema.complementToken,
      formSchema.isRedirect ? `PLR - ${formSchema.isRedirect}` : 'PLR - RESULTADO',
    );
    const leadCategoryForOffer = leadResultCategory(formSchema.isRedirect);

    if (formSchema.step === 'READY_FOR_LOAN') {
      analytics.page(resultCategory, 'ESPERANDO APROBACIÓN MEDIANTE BACKOFFICE');
      if (showComplementOffer) {
        analytics.page(leadCategoryForOffer, LEAD_OFFER_PAGE, { screen: 'lastStep' });
      }
    }
    if (formSchema.step === 'COMPANY_APROVE') {
      analytics.page(resultCategory, 'ESPERANDO APROBACIÓN DEL EMPLEADOR');
    }
    if (formSchema.step === 'MANUAL_VERIFICATION') {
      analytics.page(resultCategory, 'ESPERANDO APROBACION MANUAL POR EVALUACION INCOMPLETA');
    }
    if (formSchema.origin === 'portal-usuario') {
      history.push('/account');
      return;
    }
    if (formSchema.config.preLoanRequestConfig?.lastStepText?.autoRedirect) {
      setTimeout(() => {
        window.location.href = getRedirectUrl(
          formSchema.config,
          true,
        );
      }, 5000);
    }
  }, []);

  return (
    <Formik
      innerRef={formRef}
      initialValues={formSchema}
      onSubmit={(values) => {
        analytics.track('Click Boton', {
          initiator: 'usuario',
          text: getOriginButtonLabel(values.origin),
        });
        if (values.origin === 'portal-usuario') {
          window.location.assign(`${process.env.REACT_APP_BANCAME_PORTAL_URL}/account`);
          return;
        }
        setTimeout(() => {
          window.location.href = getRedirectUrl(
            values.config,
            true,
          );
        }, 100);
      }}
    >
      {({
        values,
      }) => (
        <Fade
          in
          {...({ timeout: 1000 })}
        >
          <Form>
            <Typography variant="h6" align="center" gutterBottom>
              {formSchema.config.preLoanRequestConfig?.lastStepText?.header}
            </Typography>
            <Typography
              style={{ marginTop: 10 }}
              gutterBottom
              variant="body1"
              align="center"
              component="div"
              data-pw="preloan-last-step-body"
            >
              {firstMessageSwitch(
                {
                  step: values.step,
                  preApproves: formSchema.preApproves,
                  firstMessage: formSchema.config.preLoanRequestConfig?.lastStepText?.firstMessage,
                  token: formSchema.PLRtoken,
                  complementOfferAvailable: showComplementOffer,
                  complementOfferPageCategory: leadCategory,
                  verificationPending: values.verificationPending,
                  partner: {
                    partnerName: formSchema.partner,
                    config: formSchema.config,
                    products: formSchema.products,
                  },
                },
              )}
            </Typography>
            {formSchema.config.preLoanRequestConfig?.lastStepText?.autoRedirect && (
            <Typography component="div" variant="body2" align="center" style={{ marginTop: 20 }}>
              Se te redirigirá en breve...
              {' '}
              <CircularProgress size={20} />
            </Typography>
            )}
            <Typography
              style={{ marginTop: 20 }}
              variant="caption"
              align="center"
              component="div"
              gutterBottom
            >
              {formSchema.config.preLoanRequestConfig?.lastStepText?.secondMessage && (
                <HTMLParser
                  html={secondMessageSwitch(
                    values.step,
                    formSchema.config.preLoanRequestConfig?.lastStepText?.secondMessage,
                  ) as string}
                />
              )}
              {' '}
              {getContactMessage(formSchema.config)}
            </Typography>
            <ComplementLeadResultDialog
              leadComplementAccepted={formSchema.leadComplementAccepted}
              ownPreApproved
              leadFirstName={formSchema.complementLeadFirstName}
              alreadyEvaluated={alreadyEvaluatedInvitee}
              partnerName={formSchema.partner}
            />
          </Form>
        </Fade>
      )}
    </Formik>
  );
}
